export const frontmatter = {
  category: 'guia',
  title: 'Comment Arriver à Ilha do Mel 2026 : Guide Complet Étape par Étape',
  excerpt:
    'Découvrez exactement comment arriver à Ilha do Mel depuis n\'importe quelle ville du Brésil. Itinéraires en voiture, bus, horaires du ferry et prix actualisés pour 2026.',
  publishedAt: '2026-05-05',
  author: 'Guia Ilha do Mel',
  tags: ['comment-arriver', 'transport', 'ferry', 'conseils-pratiques', '2026'],
  coverImage: '/images/sailboat.jpg',
  readingTime: '7 min',
};

import { BuscaPassagensWidget } from '@/components/BuscaPassagensWidget';

# Comment Arriver à Ilha do Mel 2026 : Guide Complet Étape par Étape

Ilha do Mel est l'une des destinations les plus spéciales du littoral du Paraná — mais contrairement aux plages accessibles en voiture, y arriver demande un peu de planification. La bonne nouvelle ? Le trajet est simple, bien signalisé et vaut chaque minute.

Dans ce guide complet, vous découvrirez **comment arriver à Ilha do Mel** depuis n'importe quelle ville du Brésil : en voiture, en bus ou en van. Nous parlerons aussi des horaires du ferry, des prix actualisés pour 2026 et des conseils pratiques pour ne pas perdre de temps le jour du voyage.

> **Conseil d'or :** Prévoyez au moins 1h30 entre l'arrivée à Curitiba et l'heure de départ du ferry. Le trajet de l'aéroport au Pontal do Sul prend environ 1h15 en voiture, et il est toujours bon d'avoir une marge de sécurité.

---

## Où se trouve Ilha do Mel

Ilha do Mel se trouve sur le littoral du Paraná, à environ **115 km de Curitiba** (1h15 en voiture). C'est une île sans voitures, sans routes goudronnées et sans connexion au réseau électrique continental — ce qui signifie que **on ne peut pas y aller en voiture jusqu'à l'île elle-même**.

Le point de départ obligatoire est le **Pontal do Sul**, un quartier de Paranaguá, d'où partent les ferries et bateaux qui font la traversée d'environ **30 minutes** jusqu'à l'île.

---

## Étape par étape : comment arriver à Ilha do Mel

Quelle que soit votre ville d'origine, l'itinéraire suit toujours ce schéma :

**Votre ville → Curitiba → Pontal do Sul → Ferry → Ilha do Mel**

Détaillons chaque étape :

### 1. Votre ville jusqu'à Curitiba

Curitiba est la porte d'entrée d'Ilha do Mel. La ville dispose de :

- **Aéroport International Afonso Pena (CWB)** — vols de toutes les régions du Brésil
- **Gare routière de Curitiba** — bus de toutes les capitales
- **Bon réseau routier** — accès facile en voiture par la BR-277

<BuscaPassagensWidget />

> **Quand acheter les billets :** Pour les dates de haute saison (décembre à mars et jours fériés), achetez au moins 30 jours à l'avance. Hors saison, 1–2 semaines suffisent.

### 2. Curitiba jusqu'au Pontal do Sul

Il existe trois façons de faire ce trajet :

#### En voiture (louée ou propre)

- **Distance :** 115 km
- **Temps :** 1h15 à 1h30
- **Itinéraire :** BR-277 direction Paranaguá → sortie pour Pontal do Sul
- **Stationnement :** Il y a des parkings privés au Pontal do Sul (R$ 30–50/jour)

#### En bus

- **Entreprises :** Graciosa, Cidade do Mel, Expresso do Pontal
- **Départs :** Gare routière de Curitiba ou Terminal do Boqueirão
- **Temps :** 1h45 à 2h
- **Prix :** R$ 25–40 (aller)
- **Conseil :** Certains bus s'arrêtent directement au quai du ferry ; d'autres s'arrêtent au centre de Paranaguá, nécessitant un taxi ou un van jusqu'au Pontal.

#### En van ou transfer

- **Options :** Vans partagées qui partent de l'aéroport ou du centre de Curitiba
- **Temps :** 1h15
- **Prix :** R$ 60–100 par personne (aller)
- **Avantage :** Dépose directement au quai du ferry, sans changement de transport

### 3. Pontal do Sul jusqu'à Ilha do Mel (ferry)

La traversée est le moment le plus emblématique du voyage. Le ferry traverse la Baía de Paranaguá avec vue sur la Serra do Mar et, par temps clair, on peut voir l'île s'approcher à l'horizon.

#### Horaires du ferry (2026)

| Départ Pontal do Sul | Jours |
| ------------------- | ---- |
| 08:00 | Tous |
| 10:00 | Tous |
| 12:00 | Tous |
| 14:00 | Tous |
| 16:00 | Tous |
| 18:00 | Vendredi, samedi, dimanche et jours fériés |

> **Attention :** Les horaires peuvent être réduits par grand vent ou mer agitée. Confirmez toujours la veille par le WhatsApp du ferry : (41) 99999-9999.

#### Prix de la traversée (2026)

| Type | Prix (aller) |
| ---- | ----------- |
| Adulte | R$ 35 |
| Enfant (5–10 ans) | R$ 18 |
| Enfant jusqu'à 4 ans | Gratuit |
| Résident de l'île | R$ 15 |

> **Important :** Le billet est uniquement aller simple. Au retour, vous achetez un autre billet directement sur le ferry ou au quai de l'île. Apportez de l'argent liquide — le Wi-Fi du ferry ne fonctionne pas toujours pour le paiement par carte.

---

## Conseils pratiques pour le jour du voyage

### Arrivez tôt pour le premier ferry

Le ferry de **08:00** est le plus tranquille. Vous arrivez sur l'île vers 08h30, avec toute la journée devant vous, et évitez les files qui se forment aux ferries de la mi-journée — surtout le samedi et les week-ends prolongés.

### Quoi emporter dans votre sac à main

Sur le ferry, vous porterez vos propres bagages (il n'y a pas de service de porteur). Donc :

- Utilisez un sac à dos au lieu d'une valise à roulettes (les rues de l'île sont sablonneuses)
- Gardez de l'eau et de la crème solaire facilement accessibles
- Ayez l'argent du billet séparé

### Pas de réseau téléphonique pendant la traversée

Profitez des 30 minutes de ferry pour vous déconnecter. Le signal internet disparaît au milieu de la baie et ne revient que quand l'île apparaît à l'horizon.

---

## Combien de temps prévoir pour le voyage complet ?

Si vous venez d'un autre État, planifiez ainsi :

| Étape | Temps estimé |
| ----- | -------------- |
| Vol jusqu'à Curitiba | De 1h à 3h (selon l'origine) |
| Aéroport → Pontal do Sul | 1h15 à 1h30 |
| Attente du ferry | 15–45 min |
| Traversée en ferry | 30 min |
| **Total** | **De 3h à 5h30** |

> **Recommandation :** Si votre vol arrive à Curitiba avant 14h00, vous attrapez le ferry de 16h00 tranquillement. Si vous arrivez après 15h00, envisagez de passer la nuit à Curitiba ou Paranaguá et de prendre le premier ferry le lendemain.

---

## Et si je veux y aller en voiture jusqu'à l'île ?

**Impossible.** Ilha do Mel est une zone de préservation environnementale et il n'y a ni pont ni route d'accès. Tous les véhicules restent sur le continent. Le seul moyen d'y arriver est en ferry ou en bateau.

---

## Conclusion

Arriver à Ilha do Mel fait partie de l'expérience. Le trajet est simple, bien organisé et, avec un peu de planification, vous évitez les files d'attente et profitez au mieux de votre temps sur l'île.

**Résumé de ce que vous devez savoir :**

1. Prenez l'avion ou le bus jusqu'à **Curitiba**
2. Prenez une voiture, un bus ou un van jusqu'au **Pontal do Sul** (1h15)
3. Embarquez sur le **ferry** (30 min de traversée)
4. Arrivez sur l'île et profitez !

Vous voulez planifier chaque détail de votre voyage ? Consultez notre guide complet d'[Ilha do Mel](/) avec des conseils sur où loger, quoi faire et combien ça coûte.
